import * as React from 'react'

import {
  RadioGroup,
  SquaredRadioInput,
  DropdownOption,
  Swatch,
  ImageSwatch,
} from '@thg-commerce/gravity-elements'

import {
  SwatchShape,
  ColourChoice,
} from '@thg-commerce/gravity-elements/Swatch'

import {
  ProductOptionContainer,
  SkeletonProductOption,
  StyledDropdown,
} from './styles'

export interface Choice {
  image?: string
  key: string
  colour?: string | null
  title: string
  disabled?: boolean
}

export interface Option {
  type: 'RADIO' | 'DROPDOWN' | 'IMAGE'
  label: string
  key: string
  selectedChoice?: string
  placeholder?: string
  choices: Choice[]
}

export interface SelectedOptions {
  [key: string]: string
}

export interface ProductOptionsProps {
  onOptionChange: (key: string, value: string) => void
  onQuantityChange?: (quantity: number) => void
  options: Option[]
  selectedOptions?: SelectedOptions
  hideProductOptionsSwatch?: boolean
  loading?: boolean
  forceDropDowns?: boolean
  hideDropDownsLabel?: boolean
  isWishlistOption?: boolean
  required?: boolean
  swatchShape?: SwatchShape
  i18nText: {
    swatch: {
      unavailableText: string
      closeButtonText: string
    }
    imageSwatch: {
      showButtonText: string
      showMoreButtonText: string
      showLessButtonText: string
    }
    dropdown?: {
      unavailableText: string
    }
    wishlist?: {
      unavailableText: string
    }
  }
}

const OptionElement = (
  props: {
    option: Option
    index: number
    required?: boolean
  } & ProductOptionsProps,
) => {
  const choicesWithColour = props.option.choices.filter(
    (choice: Choice) => choice.colour,
  ) as ColourChoice[]

  switch (props.option.type) {
    case 'DROPDOWN':
      return (
        <React.Fragment>
          <StyledDropdown
            label={props.option.label}
            labelHidden={props.hideDropDownsLabel || false}
            required={props.required ?? true}
            ariaLabel={props.option.label}
            placeholder={props.option.placeholder}
            onChange={(event) => {
              props.onOptionChange(props.option.key, event.target.value)
            }}
            marginBottom={choicesWithColour.length > 0 || props.index >= 0}
            value={props.selectedOptions?.[props.option.key]}
          >
            {props.option.choices.map((choice: Choice) => (
              <DropdownOption
                value={choice.key}
                key={choice.key}
                data-testid={`productoptions-${choice.title.replace(
                  /\s+/g,
                  '-',
                )}-dropdownoption`}
              >
                {choice.disabled
                  ? props.isWishlistOption
                    ? `${choice.title} - ${props.i18nText.wishlist?.unavailableText}`
                    : `${choice.title} - ${props.i18nText.dropdown?.unavailableText}`
                  : choice.title}
              </DropdownOption>
            ))}
          </StyledDropdown>
          {choicesWithColour.length > 0 && !props.forceDropDowns && (
            <Swatch
              shape={props.swatchShape ?? SwatchShape.Square}
              colours={choicesWithColour}
              selectedColour={props.selectedOptions?.[props.option.key]}
              onColourChange={(value: string) => {
                props.onOptionChange(props.option.key, value)
              }}
              i18nText={props.i18nText.swatch}
              displaySwatch={props.hideProductOptionsSwatch ?? false}
            />
          )}
        </React.Fragment>
      )
    case 'IMAGE':
      return (
        <ImageSwatch
          label={props.option.label}
          labelHidden={props.hideDropDownsLabel || false}
          required={true}
          ariaLabel={props.option.label}
          images={choicesWithColour}
          selectedImage={props.selectedOptions?.[props.option.key]}
          onImageChange={(value: string) => {
            props.onOptionChange(props.option.key, value)
          }}
          i18nText={props.i18nText.imageSwatch}
        />
      )
    default:
      return (
        <RadioGroup
          required={props.required ?? true}
          label={props.option.label}
          selectedValue={props.selectedOptions?.[props.option.key]}
          labelHidden={false}
          columns={2}
          getState={(value: string) =>
            props.onOptionChange(props.option.key, value)
          }
          horizontal={true}
          defaultValue={props.selectedOptions?.[props.option.key]}
        >
          {props.option.choices.map((choice: Choice, index: number) => (
            <SquaredRadioInput
              name={props.option.key}
              title={choice.title}
              value={choice.key}
              key={index}
              disabled={choice.disabled}
              borderWidth="2px"
            />
          ))}
        </RadioGroup>
      )
  }
}

export const ProductOptions = (props: ProductOptionsProps) => {
  if (props.loading) {
    return <SkeletonProductOption />
  }

  const options = React.useMemo(
    () =>
      props.options.map((option: Option, index: number) => {
        if (option.choices.length === 0) return

        return (
          <ProductOptionContainer key={index}>
            <OptionElement
              option={
                props.forceDropDowns ? { ...option, type: 'DROPDOWN' } : option
              }
              {...props}
              index={index}
            />
          </ProductOptionContainer>
        )
      }),
    [props.options],
  )
  return <React.Fragment>{options.length > 0 ? options : null}</React.Fragment>
}
